import { useHistory } from 'react-router-dom';
import emptyFunction from 'react-utils/emptyFunction';
import { useMutation, useQuery } from 'data-fetching-client';
import getIn from 'immutable-less/transmute/getIn';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import H2 from 'UIComponents/elements/headings/H2';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import UIConfirmModal from 'UIComponents/dialog/UIConfirmModal';
import { getPropertyValue } from 'framework-listing-lib/utils/crmObject';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { PLATFORM } from 'automation-ui-flow-core/flow/FlowSourceTypes';
import { joinPathAndQuery } from 'automation-ui-routing/utils';
import WorkflowReferenceModal from 'automation-ui-management/components/modals/WorkflowReferenceModal';
import { DELETE_FLOWS } from 'automation-ui-management/queries/flowQueries';
import { FLOW_REFERENCES_FIELDNAME } from 'automation-ui-management/queries/flowReferenceQueries';
import { useFlowReferences } from 'automation-ui-management/hooks/flowReferencesHook';
import { CRM_OBJECT_SEARCH_FIELD_NAME, CRM_OBJECT_SEARCH_BY_FLOW_ID } from 'automation-ui-management/queries/crmObjectQueries';
import { AutomationPlatformFlowProperties } from 'automation-ui-management/constants/CrmWorkflowProperties';
import WorkflowFetchFailureAlert from 'automation-ui-management/components/modals/alerts/WorkflowFetchFailureAlert';
import getFlowDisplayName from 'automation-ui-management/utils/getFlowDisplayName';
import { IndexSearchParams } from 'automation-ui-management/constants/SearchParams';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DeleteWorkflowModal = ({
  flowId,
  handleDismissModal,
  onSuccess = emptyFunction,
  onFailure = emptyFunction,
  shouldForwardToIndex = true
}) => {
  const history = useHistory();
  const trackEvent = useTrackEvent();
  const flowCrmObjectQuery = useQuery(CRM_OBJECT_SEARCH_BY_FLOW_ID, {
    variables: {
      flowId: String(flowId)
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });
  const crmObject = getIn(['data', CRM_OBJECT_SEARCH_FIELD_NAME], flowCrmObjectQuery);
  const [deleteFlows] = useMutation(DELETE_FLOWS, {
    onCompleted: () => {
      const objectId = crmObject && Number(getPropertyValue(crmObject, AutomationPlatformFlowProperties.OBJECT_ID));
      onSuccess({
        flowId,
        objectId
      });
      if (shouldForwardToIndex) {
        const queryParams = {
          [IndexSearchParams.DELETED_WORKFLOW]: objectId
        };
        const path = joinPathAndQuery('/view/default', queryParams);
        history.push(path);
      } else {
        handleDismissModal();
      }
    },
    onError: error => {
      onFailure(error);
      handleDismissModal();
    }
  });
  const referencesQuery = useFlowReferences({
    flowId,
    flowSource: PLATFORM
  });
  const inboundReferences = getIn(['data', FLOW_REFERENCES_FIELDNAME, 'inboundReferences'], referencesQuery);

  // DEV NOTE: Using `useTrackEvent` instead of using `TrackableButton` as this component uses `UIConfirmModal`.
  // `TrackableButton` is currently not compatible to use in conjunction with `UIConfirmModal`
  // due to the button and modal's event handling prop conflict (onClick vs onConfirm/onReject)
  const handleOnDelete = () => {
    trackEvent(Trackables.DELETED_WORKFLOW, undefined, true);
    deleteFlows({
      variables: {
        flowIdFlowSourcePairs: [{
          flowId,
          flowSource: PLATFORM
        }]
      }
    }).catch(error => {
      reportError({
        error
      });
    });
  };
  if (referencesQuery.loading || flowCrmObjectQuery.loading) {
    return null;
  }
  if (referencesQuery.error) {
    return /*#__PURE__*/_jsxs(UIModal, {
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: handleDismissModal
        }), /*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.modal.workflowReferenceModal.header"
          })
        })]
      }), /*#__PURE__*/_jsx(UIDialogBody, {
        children: /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.modal.workflowReferenceModal.referenceRequestFailed"
          })
        })
      }), /*#__PURE__*/_jsx(UIDialogFooter, {
        children: /*#__PURE__*/_jsx(UIButton, {
          use: "primary",
          type: "submit",
          onClick: handleDismissModal,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.modal.workflowReferenceModal.okButton"
          })
        })
      })]
    });
  }
  if (inboundReferences && inboundReferences.length) {
    return /*#__PURE__*/_jsx(WorkflowReferenceModal, {
      inboundReferences: inboundReferences,
      onDismissModal: handleDismissModal
    });
  }
  if (!crmObject) {
    return /*#__PURE__*/_jsxs(UIModal, {
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.modal.deleteModal.deleteButton"
          })
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: handleDismissModal
        })]
      }), /*#__PURE__*/_jsx(UIDialogBody, {
        className: "p-y-10",
        children: /*#__PURE__*/_jsx(WorkflowFetchFailureAlert
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        , {
          handleRefetch: () => flowCrmObjectQuery.refetch()
        })
      })]
    });
  }
  const enrolledLastWeekCount = Number(getPropertyValue(crmObject, AutomationPlatformFlowProperties.ENROLLED_PAST_7_DAYS));
  const isEnabled = getPropertyValue(crmObject, AutomationPlatformFlowProperties.IS_ENABLED) === 'true';
  const name = getFlowDisplayName(crmObject);
  const message = /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: "automation-ui-management.modal.deleteModal.header",
    options: {
      workflowName: name
    }
  });
  const description = /*#__PURE__*/_jsx(Text, {
    marginBlockEnd: "400",
    children: isEnabled && enrolledLastWeekCount > 0 ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "automation-ui-management.modal.deleteModal.enabledWorkflowsInfo",
      options: {
        count: enrolledLastWeekCount
      }
    }) : /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "automation-ui-management.modal.deleteModal.info",
      options: {
        workflowName: name
      }
    })
  });
  return /*#__PURE__*/_jsx(UIConfirmModal, {
    "data-test-id": "delete-modal-root",
    use: 'danger',
    size: "auto",
    message: message,
    confirmLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-management.modal.deleteModal.deleteButton"
    }),
    disableConfirm: !crmObject,
    confirmUse: "danger",
    description: description,
    onConfirm: handleOnDelete,
    onReject: handleDismissModal,
    rejectLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-management.modal.deleteModal.cancelButton"
    })
  });
};
export default DeleteWorkflowModal;